import { useState } from 'react';
import { AlertTriangle } from 'lucide-react';

import { useTranslation } from '../../i18n';
import { useMuteUser } from '../../hooks/queries/useUserQueries';
import { IUser } from '../../types';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';

interface IUserMuteDialogProps {
  isOpen: boolean;
  onClose: () => void;
  user: IUser | null;
}

const DURATION_MAP: Record<string, number> = {
  '1h': 60 * 60 * 1000,
  '24h': 24 * 60 * 60 * 1000,
  '7d': 7 * 24 * 60 * 60 * 1000,
  '30d': 30 * 24 * 60 * 60 * 1000,
};

const UserMuteDialog = ({ isOpen, onClose, user }: IUserMuteDialogProps) => {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const [duration, setDuration] = useState('indefinite');
  const muteUser = useMuteUser();

  const handleConfirm = async () => {
    if (!user) return;

    let mutedUntil: string | undefined;

    if (duration !== 'indefinite' && DURATION_MAP[duration]) {
      mutedUntil = new Date(Date.now() + DURATION_MAP[duration]).toISOString();
    }

    await muteUser.mutateAsync({ userId: user.id, reason, mutedUntil });
    setReason('');
    setDuration('indefinite');
    onClose();
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setReason('');
      setDuration('indefinite');
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent
        className="sm:max-w-[480px]"
        data-testid="UserManagement_List_muteModal"
      >
        <DialogHeader>
          <DialogTitle>{t('users.mute_title')}</DialogTitle>
          <DialogDescription>
            {t('users.mute_confirm', { name: user?.displayName || '' })}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 shrink-0" />
              <div>
                <p className="text-sm font-medium text-red-900">
                  {t('users.mute_warning_title')}
                </p>
                <p className="text-sm text-red-700 mt-1">
                  {t('users.mute_warning')}
                </p>
              </div>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('users.mute_duration')}
            </label>
            <Select value={duration} onValueChange={setDuration}>
              <SelectTrigger data-testid="UserManagement_List_muteModalDurationSelect">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem
                  value="indefinite"
                  data-testid="UserManagement_List_muteModalDurationSelectOption_indefinite"
                >
                  {t('users.mute_indefinite')}
                </SelectItem>
                <SelectItem
                  value="1h"
                  data-testid="UserManagement_List_muteModalDurationSelectOption_1h"
                >
                  {t('users.mute_1_hour')}
                </SelectItem>
                <SelectItem
                  value="24h"
                  data-testid="UserManagement_List_muteModalDurationSelectOption_24h"
                >
                  {t('users.mute_24_hours')}
                </SelectItem>
                <SelectItem
                  value="7d"
                  data-testid="UserManagement_List_muteModalDurationSelectOption_7d"
                >
                  {t('users.mute_7_days')}
                </SelectItem>
                <SelectItem
                  value="30d"
                  data-testid="UserManagement_List_muteModalDurationSelectOption_30d"
                >
                  {t('users.mute_30_days')}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('users.mute_reason')}
            </label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t('users.mute_reason_placeholder')}
              rows={3}
              data-testid="UserManagement_List_muteModalReasonTextArea"
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            data-testid="UserManagement_List_muteModalCancelButton"
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={muteUser.isPending}
            data-testid="UserManagement_List_muteModalConfirmButton"
          >
            {muteUser.isPending ? t('common.saving') : t('users.confirm_mute')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default UserMuteDialog;
